import { useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function Hero() {
  const { t } = useI18n();
  const heroRef = useRef<HTMLElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const greetingRef = useRef<HTMLSpanElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  const underlineRef = useRef<HTMLDivElement>(null);
  const taglineRef = useRef<HTMLParagraphElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Split a text node into inline-block word spans for staggered reveals
      const splitWords = (el: HTMLElement | null, wordClass: string): HTMLElement[] => {
        if (!el) return [];
        const words = (el.textContent || '').trim().split(/\s+/);
        el.textContent = '';
        return words.map((word, i) => {
          const span = document.createElement('span');
          span.className = `${wordClass} hero-word`;
          span.innerHTML = word + (i < words.length - 1 ? '&nbsp;' : '');
          el.appendChild(span);
          return span;
        });
      };

      const greetingWords = splitWords(greetingRef.current, 'hero-greeting-word');
      const nameWords = splitWords(nameRef.current, 'hero-name-word');

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // 1. Badge de estado — aparece con expansión de tracking
      tl.fromTo(
        badgeRef.current,
        { opacity: 0, y: -14, letterSpacing: '0.6em' },
        { opacity: 1, y: 0, letterSpacing: '0.32em', duration: 0.8 },
        0.15
      );

      // 2. Greeting — palabras entran con blur → sharp + stagger
      tl.fromTo(
        greetingWords,
        { opacity: 0, y: 20, filter: 'blur(8px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.6, stagger: 0.07 },
        0.45
      );

      // 3. Nombre — palabras entran con escala + blur, efecto "manifestación"
      tl.fromTo(
        nameWords,
        { opacity: 0, y: 36, scale: 1.12, filter: 'blur(14px)' },
        { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)', duration: 0.8, stagger: 0.1 },
        0.85
      );

      // 4. Flash de glow tipo "glitch" al terminar el nombre
      tl.fromTo(
        nameRef.current,
        {
          textShadow:
            '0 0 24px rgba(0,255,136,0.9), 0 0 60px rgba(0,212,255,0.7), 0 0 8px rgba(255,255,255,0.4)',
        },
        { textShadow: '0 0 0px rgba(0,0,0,0)', duration: 1.4, ease: 'power2.out' },
        1.3
      );

      // 5. Barra subrayado — se dibuja sola
      tl.fromTo(
        underlineRef.current,
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1, ease: 'power3.inOut' },
        1.35
      );

      // 6. Tagline
      tl.fromTo(taglineRef.current, { opacity: 0, y: 22 }, { opacity: 1, y: 0, duration: 0.8 }, 1.7);

      // 7. Indicador de scroll
      tl.fromTo(scrollRef.current, { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.8 }, 2.0);

      gsap.to(scrollRef.current, {
        y: 10,
        duration: 1.5,
        ease: 'power1.inOut',
        yoyo: true,
        repeat: -1,
      });

      // 8. Spotlight que sigue el mouse (solo dispositivos con puntero fino)
      const finePointer = window.matchMedia('(pointer: fine)').matches;
      if (finePointer && spotlightRef.current && heroRef.current) {
        const xTo = gsap.quickTo(spotlightRef.current, 'x', { duration: 0.5, ease: 'power3.out' });
        const yTo = gsap.quickTo(spotlightRef.current, 'y', { duration: 0.5, ease: 'power3.out' });
        const heroEl = heroRef.current;

        const onMove = (e: MouseEvent) => {
          const rect = heroEl.getBoundingClientRect();
          xTo(e.clientX - rect.left - 320);
          yTo(e.clientY - rect.top - 320);
          gsap.to(spotlightRef.current, { opacity: 1, duration: 0.3, overwrite: 'auto' });
        };
        const onLeave = () => gsap.to(spotlightRef.current, { opacity: 0, duration: 0.5 });

        heroEl.addEventListener('mousemove', onMove);
        heroEl.addEventListener('mouseleave', onLeave);
        return () => {
          heroEl.removeEventListener('mousemove', onMove);
          heroEl.removeEventListener('mouseleave', onLeave);
        };
      }

      // 9. Parallax al hacer scroll
      ScrollTrigger.create({
        trigger: heroRef.current,
        start: 'top top',
        end: 'bottom top',
        scrub: 1,
        onUpdate: (self) => {
          const p = self.progress;
          if (nameRef.current) {
            gsap.set(nameRef.current, { y: p * -80, opacity: 1 - p, scale: 1 - p * 0.06 });
          }
          if (greetingRef.current) {
            gsap.set(greetingRef.current, { y: p * -50, opacity: 1 - p });
          }
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      id="hero"
      className="hero"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        padding: '2rem',
        overflow: 'hidden',
      }}
      aria-labelledby="hero-title"
    >
      <div
        className="hero-bg"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, rgba(0, 255, 136, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Scanlines sutiles cyberpunk */}
      <div className="hero-scanlines" aria-hidden="true" />
      {/* Barrido de luz al cargar */}
      <div className="hero-sweep" aria-hidden="true" />
      {/* Spotlight que sigue al mouse */}
      <div ref={spotlightRef} className="hero-spotlight" aria-hidden="true" />

      <div className="hero-content" style={{ textAlign: 'center', zIndex: 2, maxWidth: '900px' }}>
        {/* Badge de estado */}
        <div ref={badgeRef} className="hero-badge" role="status">
          <span className="hero-badge-dot" />
          <span className="hero-badge-text">{t('hero.badge')}</span>
          <span className="hero-cursor" aria-hidden="true">
            ▮
          </span>
        </div>

        {/* Greeting — pequeño, monoespaciado, con shimmer */}
        <span ref={greetingRef} className="hero-greeting">
          {t('hero.greeting')}
        </span>

        {/* Nombre — el h1, compacto pero con presencia */}
        <h1 ref={nameRef} id="hero-title" className="hero-name">
          {t('hero.name')}
        </h1>

        {/* Barra subrayado animada */}
        <div ref={underlineRef} className="hero-underline" aria-hidden="true" />

        <p
          ref={taglineRef}
          className="hero-tagline"
          style={{
            fontSize: 'clamp(1rem, 1.9vw, 1.3rem)',
            color: 'var(--fg-secondary)',
            maxWidth: '620px',
            margin: '0 auto 2.5rem',
            lineHeight: 1.6,
            fontWeight: 300,
          }}
        >
          {t('hero.tagline')}
        </p>

        <div
          ref={scrollRef}
          className="scroll-indicator"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem',
            color: 'var(--fg-muted)',
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.2em',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          <span>{t('hero.scroll')}</span>
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animation: 'bounce 2s ease-in-out infinite' }}
          >
            <path d="M12 5v14M19 12l-7 7-7-7" />
          </svg>
          <style>{`
            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
              40% { transform: translateY(-10px); }
              60% { transform: translateY(-5px); }
            }
          `}</style>
        </div>
      </div>
    </section>
  );
}
